import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Edit,
  ExternalLink,
  Filter,
  History,
  Loader2,
  MessageCircle,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['planned', 'checking', 'available', 'claimed', 'taken', 'blocked', 'needs_review', 'retired'];
const PRIORITY_OPTIONS = ['critical', 'high', 'medium', 'low', 'future'];
const PLATFORM_OPTIONS = ['whatsapp', 'instagram', 'threads', 'facebook', 'linkedin', 'x', 'telegram', 'tiktok', 'youtube', 'bluesky', 'github', 'discord'];
const NOTE_REQUIRED = new Set(['taken', 'blocked', 'needs_review', 'retired']);

const statusStyles = {
  planned: 'bg-gray-100 text-gray-700 border-gray-200',
  checking: 'bg-blue-100 text-blue-700 border-blue-200',
  available: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  claimed: 'bg-green-100 text-green-700 border-green-200',
  taken: 'bg-amber-100 text-amber-700 border-amber-200',
  blocked: 'bg-red-100 text-red-700 border-red-200',
  needs_review: 'bg-violet-100 text-violet-700 border-violet-200',
  retired: 'bg-slate-100 text-slate-700 border-slate-200'
};

const priorityStyles = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  medium: 'bg-blue-50 text-blue-700 border-blue-200',
  low: 'bg-gray-50 text-gray-700 border-gray-200',
  future: 'bg-slate-50 text-slate-700 border-slate-200'
};

const blankCandidate = {
  brand_name: '',
  brand_family: 'Product',
  platform: 'whatsapp',
  username: '',
  priority: 'medium',
  status: 'planned',
  owner_name: '',
  account_email_label: '',
  mfa_status: 'unknown',
  recovery_status: 'unknown',
  evidence_url: '',
  evidence_note: '',
  notes: ''
};

function getAuthHeaders() {
  const token = localStorage.getItem('ezerpToken') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : ''
  };
}

function labelize(value) {
  return String(value || '').replace(/_/g, ' ');
}

function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={statusStyles[status] || statusStyles.planned}>
      {labelize(status)}
    </Badge>
  );
}

function PriorityBadge({ priority }) {
  return (
    <Badge variant="outline" className={priorityStyles[priority] || priorityStyles.medium}>
      {labelize(priority)}
    </Badge>
  );
}

function MetricCard({ title, value, helper, icon: Icon, accent = 'text-teal-600', bg = 'bg-teal-50' }) {
  return (
    <Card className="border border-border shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
            <Icon className={`h-5 w-5 ${accent}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold leading-tight">{value}</p>
            {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BrandProtection() {
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [overview, setOverview] = useState(null);
  const [brands, setBrands] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCandidateDialog, setShowCandidateDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEventsDialog, setShowEventsDialog] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDraft, setCandidateDraft] = useState(blankCandidate);
  const [statusDraft, setStatusDraft] = useState({ status: 'checking', note: '', evidence_url: '', evidence_note: '' });
  const [filters, setFilters] = useState({ platform: 'whatsapp', brand: 'all', status: 'all', priority: 'all', search: '' });

  const requestJson = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_BASE}/api/crm/brand-protection${path}`, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...(options.headers || {})
      }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || `Request failed (${response.status})`);
    }
    return data;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params.set(key, value);
      });
      const [overviewData, brandData, candidateData] = await Promise.all([
        requestJson('/overview'),
        requestJson('/brands'),
        requestJson(`/candidates?${params.toString()}`)
      ]);
      setOverview(overviewData);
      setBrands(Array.isArray(brandData) ? brandData : []);
      setCandidates(Array.isArray(candidateData) ? candidateData : []);
    } catch (error) {
      toast.error(error.message || 'Failed to load brand protection data');
    } finally {
      setLoading(false);
    }
  }, [filters, requestJson]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const visibleCandidates = useMemo(() => {
    if (activeTab === 'whatsapp') return candidates.filter((item) => item.platform === 'whatsapp');
    if (activeTab === 'social') return candidates.filter((item) => item.platform !== 'whatsapp');
    return candidates;
  }, [activeTab, candidates]);

  const openCreate = () => {
    setEditingCandidate(null);
    setCandidateDraft(blankCandidate);
    setShowCandidateDialog(true);
  };

  const openEdit = (candidate) => {
    setEditingCandidate(candidate);
    setCandidateDraft({
      brand_name: candidate.brand_name || '',
      brand_family: candidate.brand_family || 'Product',
      platform: candidate.platform || 'whatsapp',
      username: candidate.username || '',
      priority: candidate.priority || 'medium',
      status: candidate.status || 'planned',
      owner_name: candidate.owner_name || '',
      account_email_label: candidate.account_email_label || '',
      mfa_status: candidate.mfa_status || 'unknown',
      recovery_status: candidate.recovery_status || 'unknown',
      evidence_url: candidate.evidence_url || '',
      evidence_note: candidate.evidence_note || '',
      notes: candidate.notes || ''
    });
    setShowCandidateDialog(true);
  };

  const saveCandidate = async () => {
    if (!candidateDraft.brand_name.trim() || !candidateDraft.username.trim()) {
      toast.error('Brand and username are required');
      return;
    }
    setSaving(true);
    try {
      if (editingCandidate) {
        await requestJson(`/candidates/${editingCandidate.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            brand_name: candidateDraft.brand_name,
            brand_family: candidateDraft.brand_family,
            priority: candidateDraft.priority,
            owner_name: candidateDraft.owner_name,
            account_email_label: candidateDraft.account_email_label,
            mfa_status: candidateDraft.mfa_status,
            recovery_status: candidateDraft.recovery_status,
            evidence_url: candidateDraft.evidence_url,
            evidence_note: candidateDraft.evidence_note,
            notes: candidateDraft.notes
          })
        });
        toast.success('Candidate updated');
      } else {
        await requestJson('/candidates', {
          method: 'POST',
          body: JSON.stringify(candidateDraft)
        });
        toast.success('Candidate created');
      }
      setShowCandidateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to save candidate');
    } finally {
      setSaving(false);
    }
  };

  const openStatus = (candidate) => {
    setSelectedCandidate(candidate);
    setStatusDraft({
      status: candidate.status === 'planned' ? 'checking' : candidate.status,
      note: '',
      evidence_url: candidate.evidence_url || '',
      evidence_note: candidate.evidence_note || ''
    });
    setShowStatusDialog(true);
  };

  const saveStatus = async () => {
    if (NOTE_REQUIRED.has(statusDraft.status) && !statusDraft.note.trim()) {
      toast.error(`${labelize(statusDraft.status)} requires a note`);
      return;
    }
    setSaving(true);
    try {
      await requestJson(`/candidates/${selectedCandidate.id}/status`, {
        method: 'POST',
        body: JSON.stringify(statusDraft)
      });
      toast.success('Status updated');
      setShowStatusDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const openEvents = async (candidate) => {
    setSelectedCandidate(candidate);
    setEvents([]);
    setShowEventsDialog(true);
    try {
      const data = await requestJson(`/candidates/${candidate.id}/events`);
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || 'Failed to load audit history');
    }
  };

  const runSeed = async () => {
    setSaving(true);
    try {
      const data = await requestJson('/seed', { method: 'POST', body: JSON.stringify({}) });
      toast.success(`Seed checked: ${data.created_candidates || 0} candidates added`);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Seed failed');
    } finally {
      setSaving(false);
    }
  };

  const exportJson = async () => {
    try {
      const data = await requestJson('/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `brand-protection-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Checklist exported');
    } catch (error) {
      toast.error(error.message || 'Export failed');
    }
  };

  if (loading && !overview) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-brand-protection">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">Brand Protection</h1>
          <p className="mt-1 text-muted-foreground">
            WhatsApp username tracking first, with manual verification and audit history.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2" disabled={loading}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" onClick={runSeed} className="gap-2" disabled={saving}>
            <ShieldCheck className="h-4 w-4" />
            Check Seed
          </Button>
          <Button variant="outline" onClick={exportJson} className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
          <Button onClick={openCreate} className="gap-2 bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4" />
            New Candidate
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard title="Candidates" value={overview?.total_candidates || 0} helper="Persisted records" icon={MessageCircle} />
        <MetricCard title="Critical Open" value={overview?.critical_open || 0} helper="Not claimed or retired" icon={AlertTriangle} accent="text-red-600" bg="bg-red-50" />
        <MetricCard title="Claimed" value={overview?.claimed || 0} helper="Manually confirmed" icon={CheckCircle2} accent="text-green-600" bg="bg-green-50" />
        <MetricCard title="Needs Review" value={overview?.needs_review || 0} helper="Requires attention" icon={UserCheck} accent="text-violet-600" bg="bg-violet-50" />
        <MetricCard title="WhatsApp" value={`${overview?.whatsapp_completion_percent || 0}%`} helper={`${overview?.whatsapp_claimed || 0}/${overview?.whatsapp_total || 0} claimed`} icon={ShieldCheck} accent="text-blue-600" bg="bg-blue-50" />
      </div>

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-teal-600" />
                Reservation Checklist
              </CardTitle>
              <CardDescription>{overview?.manual_truth_note}</CardDescription>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              <Input
                placeholder="Search"
                value={filters.search}
                onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
              />
              <Select value={filters.platform} onValueChange={(value) => setFilters((current) => ({ ...current, platform: value }))}>
                <SelectTrigger><SelectValue placeholder="Platform" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All platforms</SelectItem>
                  {PLATFORM_OPTIONS.map((item) => <SelectItem key={item} value={item}>{labelize(item)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.brand} onValueChange={(value) => setFilters((current) => ({ ...current, brand: value }))}>
                <SelectTrigger><SelectValue placeholder="Brand" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All brands</SelectItem>
                  {brands.map((brand) => <SelectItem key={brand.id} value={brand.brand_name}>{brand.brand_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={(value) => setFilters((current) => ({ ...current, status: value }))}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_OPTIONS.map((item) => <SelectItem key={item} value={item}>{labelize(item)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.priority} onValueChange={(value) => setFilters((current) => ({ ...current, priority: value }))}>
                <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  {PRIORITY_OPTIONS.map((item) => <SelectItem key={item} value={item}>{labelize(item)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid h-auto w-full grid-cols-3">
              <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
              <TabsTrigger value="social">Social Later</TabsTrigger>
              <TabsTrigger value="all">All Records</TabsTrigger>
            </TabsList>
            <TabsContent value={activeTab}>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Brand</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>MFA</TableHead>
                      <TableHead>Evidence</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleCandidates.map((candidate) => (
                      <TableRow key={candidate.id}>
                        <TableCell>
                          <div className="font-medium">{candidate.brand_name}</div>
                          <div className="text-xs text-muted-foreground">{candidate.brand_family}</div>
                        </TableCell>
                        <TableCell><Badge variant="secondary">{labelize(candidate.platform)}</Badge></TableCell>
                        <TableCell className="font-mono">@{candidate.username}</TableCell>
                        <TableCell><PriorityBadge priority={candidate.priority} /></TableCell>
                        <TableCell><StatusBadge status={candidate.status} /></TableCell>
                        <TableCell>{candidate.owner_name || '-'}</TableCell>
                        <TableCell>
                          <div className="space-y-1 text-xs">
                            <div>MFA: {labelize(candidate.mfa_status)}</div>
                            <div>Recovery: {labelize(candidate.recovery_status)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {candidate.evidence_url ? (
                            <a href={candidate.evidence_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                              Open <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openStatus(candidate)} title="Change status">
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEvents(candidate)} title="View audit history">
                              <History className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(candidate)} title="Edit candidate">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!visibleCandidates.length && (
                      <TableRow>
                        <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                          No candidates match the current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={showCandidateDialog} onOpenChange={setShowCandidateDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingCandidate ? 'Edit Candidate' : 'New Candidate'}</DialogTitle>
            <DialogDescription>Track a brand username manually. Do not store passwords, tokens, OTPs, or recovery codes.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Brand</Label>
              <Input value={candidateDraft.brand_name} onChange={(event) => setCandidateDraft({ ...candidateDraft, brand_name: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Brand family</Label>
              <Input value={candidateDraft.brand_family} onChange={(event) => setCandidateDraft({ ...candidateDraft, brand_family: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={candidateDraft.platform} onValueChange={(value) => setCandidateDraft({ ...candidateDraft, platform: value })} disabled={!!editingCandidate}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map((item) => <SelectItem key={item} value={item}>{labelize(item)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={candidateDraft.username} onChange={(event) => setCandidateDraft({ ...candidateDraft, username: event.target.value })} disabled={!!editingCandidate} />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={candidateDraft.priority} onValueChange={(value) => setCandidateDraft({ ...candidateDraft, priority: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((item) => <SelectItem key={item} value={item}>{labelize(item)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Initial status</Label>
              <Select value={candidateDraft.status} onValueChange={(value) => setCandidateDraft({ ...candidateDraft, status: value })} disabled={!!editingCandidate}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((item) => <SelectItem key={item} value={item}>{labelize(item)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Owner</Label>
              <Input value={candidateDraft.owner_name} onChange={(event) => setCandidateDraft({ ...candidateDraft, owner_name: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Account email label</Label>
              <Input value={candidateDraft.account_email_label} onChange={(event) => setCandidateDraft({ ...candidateDraft, account_email_label: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>MFA status</Label>
              <Input value={candidateDraft.mfa_status} onChange={(event) => setCandidateDraft({ ...candidateDraft, mfa_status: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Recovery status</Label>
              <Input value={candidateDraft.recovery_status} onChange={(event) => setCandidateDraft({ ...candidateDraft, recovery_status: event.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Evidence URL</Label>
              <Input value={candidateDraft.evidence_url} onChange={(event) => setCandidateDraft({ ...candidateDraft, evidence_url: event.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Textarea className="min-h-[90px]" value={candidateDraft.notes} onChange={(event) => setCandidateDraft({ ...candidateDraft, notes: event.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCandidateDialog(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={saveCandidate} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              {selectedCandidate ? `${selectedCandidate.brand_name} / @${selectedCandidate.username}` : 'Manual status update'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusDraft.status} onValueChange={(value) => setStatusDraft({ ...statusDraft, status: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((item) => <SelectItem key={item} value={item}>{labelize(item)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Evidence URL</Label>
              <Input value={statusDraft.evidence_url} onChange={(event) => setStatusDraft({ ...statusDraft, evidence_url: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Evidence note</Label>
              <Textarea value={statusDraft.evidence_note} onChange={(event) => setStatusDraft({ ...statusDraft, evidence_note: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Change note{NOTE_REQUIRED.has(statusDraft.status) ? ' required' : ''}</Label>
              <Textarea className="min-h-[96px]" value={statusDraft.note} onChange={(event) => setStatusDraft({ ...statusDraft, note: event.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={saveStatus} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEventsDialog} onOpenChange={setShowEventsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Audit History</DialogTitle>
            <DialogDescription>
              {selectedCandidate ? `${selectedCandidate.brand_name} / @${selectedCandidate.username}` : 'Candidate events'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] space-y-3 overflow-auto">
            {events.map((event) => (
              <div key={event.id} className="rounded-md border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium">{labelize(event.event_type)}</div>
                  <div className="text-xs text-muted-foreground">{event.created_at}</div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {event.old_status && <StatusBadge status={event.old_status} />}
                  {event.new_status && <StatusBadge status={event.new_status} />}
                </div>
                {event.note && <p className="mt-2 text-muted-foreground">{event.note}</p>}
              </div>
            ))}
            {!events.length && <div className="rounded-md border p-6 text-center text-muted-foreground">No audit events found.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
